import { type Round } from '@h2h/shared';
import { Trophy } from '../../../components/ui/icons';
import { signedPoints } from '../../../lib/format';

export function roundDateRange(round: Round): string {
  return new Date(round.firstKickoffAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** The round's scored summary. When at least one card lands every market it's a
 *  full-blast celebratory banner (green gradient, glow, sweeping sheen, trophy) —
 *  a perfect match should feel like a moment. Otherwise it's a calm, neutral
 *  history strip: how many of your cards have settled so far and the points they
 *  banked. We deliberately DON'T frame the neutral state as "0/1 perfect cards" —
 *  that reads like a quota you're failing, when it's really just a scored log. */
export function RoundRecap({
  perfect,
  settled,
  built,
  points
}: {
  perfect: number;
  settled: number;
  built: number;
  points: number;
}) {
  if (perfect > 0) {
    const label = perfect === 1 ? 'perfect card' : 'perfect cards';
    return (
      <div className="relative mt-3 animate-h2h-pop overflow-hidden rounded-[18px] bg-[linear-gradient(135deg,#2EDB7B_0%,#10B981_48%,#0BA078_100%)] px-[16px] py-[13px] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_18px_34px_-14px_rgba(11,160,120,0.75)]">
        {/* sweeping sheen + corner glow for the “wow” */}
        <div className="pointer-events-none absolute inset-y-0 w-[42%] animate-h2h-sheen bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)]" />
        <span className="pointer-events-none absolute -right-7 -top-10 h-28 w-28 rounded-full bg-white/25 blur-2xl" />
        <div className="relative flex items-center justify-between gap-3">
          <div className="flex items-center gap-[12px]">
            <span className="grid h-[40px] w-[40px] place-items-center rounded-[13px] bg-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] ring-1 ring-white/35">
              <Trophy width={21} height={21} strokeWidth={2.2} className="text-white drop-shadow-[0_1px_2px_rgba(6,55,31,0.35)]" />
            </span>
            <div className="leading-[1.12]">
              <div className="flex items-baseline gap-[6px]">
                <span className="num text-[23px] font-extrabold tracking-[-0.02em] text-white drop-shadow-[0_1px_2px_rgba(6,55,31,0.25)]">
                  {perfect}
                </span>
                <span className="text-[12px] font-extrabold uppercase tracking-[0.05em] text-white/90">{label}</span>
              </div>
              <div className="text-[11px] font-semibold text-white/75">Every leg hit — perfect card</div>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <span className="num text-[19px] font-extrabold tracking-[-0.01em] text-white">{signedPoints(points)}</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-white/70">points</span>
          </div>
        </div>
      </div>
    );
  }

  const partial = built > settled;
  const label = `card${settled === 1 && !partial ? '' : 's'} settled`;

  return (
    <div className="mt-3 flex items-center justify-between rounded-[16px] border border-black/10 bg-wash px-[14px] py-[11px] shadow-tile">
      <div className="flex items-center gap-[9px]">
        <span className="num text-[18px] font-extrabold tracking-[-0.02em] text-ink">
          {settled}
          {partial && <span className="text-ink3">/{built}</span>}
        </span>
        <span className="text-[12px] font-bold leading-[1.2] text-ink2">{label}</span>
      </div>
      <div className="flex flex-col items-end">
        <span className="num text-[15px] font-extrabold tracking-[-0.01em] text-ink">{signedPoints(points)}</span>
        <span className="text-[10px] font-bold uppercase tracking-[0.05em] text-ink3">points</span>
      </div>
    </div>
  );
}
